import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/react'
import { useAuth } from '../../components/provider/OIDCProvider'
import { ActiveRole } from '../activeRole'

/**
 * Orchestrates switching the active persona for a dual-role (student/advisor)
 * user: clears the Apollo cache first, then activates the new role and navigates
 * home. The token is untouched — same identity, no re-auth.
 *
 * Ordering matters and fixes two defects at once:
 *  - Clear the cache BEFORE flipping the persona. Advisor-only UI — notably the
 *    admin student-search box, which renders only while the effective persona is
 *    advisor (`userIsAdmin` follows `activeRole` for dual-role users) — must not
 *    mount against a store that is mid-teardown/refetch. Interacting with it in
 *    that window re-resolved the persona to the student default and stranded the
 *    user on their own plan board (CDE-1227). Clearing first means the new
 *    persona's UI never renders until the cache is stable again; the app's
 *    `isPersonaPending` loader gate covers the settings refetch that follows.
 *  - Activate the new role BEFORE `history.push('/')`. The home route ("/") is
 *    persona-dependent (a student lands on their plan board, an advisor on the
 *    dashboard), so navigating first under the old persona let the student
 *    landing-redirect bounce the URL back to the plan board (CDE-1221).
 *
 * This matches the ordering in useRoleSyncAcrossTabs (the cross-tab path).
 */
const useRoleSwitch = () => {
  const { activeRole, setActiveRole } = useAuth()
  const client = useApolloClient()
  const history = useHistory()

  const switchRole = useCallback(
    async (next: ActiveRole) => {
      if (!setActiveRole || next === activeRole) {
        return
      }
      // Clear persona-scoped data first, while the old persona is still active,
      // so no advisor-only UI is interactable mid-teardown (CDE-1227).
      // Best-effort: proceed with the switch even if the clear fails, but report
      // the failure — leaving the cache in an unknown state across a persona
      // change is exactly the teardown-timing hazard that caused the bug.
      try {
        await client.clearStore()
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: 'role-switch' } })
      }
      // Then flip the persona and navigate home. Setting the role before the
      // push means "/" renders under the new persona rather than bouncing back
      // via the old persona's landing redirect (CDE-1221).
      setActiveRole(next)
      history.push('/')
      mixpanel.track('Role Switched', { to: next })
    },
    [activeRole, setActiveRole, client, history]
  )

  return { switchRole }
}

export default useRoleSwitch
